import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { JsonLdScript } from '../../../components/seo/JsonLdScript';
import {
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
} from '../../../components/seo/jsonLd';
import { usePrerenderReady } from '../../../components/seo/usePrerenderReady';
import { getDefaultOgImageUrl, getPublicSiteUrl } from '../siteUrl';

const DEFAULT_TITLE = 'Charlee.ai';
const DEFAULT_DESCRIPTION = 'Charlee.ai';

function trimNonEmpty(value) {
  if (value == null || typeof value !== 'string') return '';
  const t = value.trim();
  return t || '';
}

function articleHeadlineFromTitle(pageTitle) {
  const stripped = pageTitle.replace(/\s*\|\s*Charlee\.ai\s*$/i, '').trim();
  return stripped || pageTitle;
}

function isValidDateString(value) {
  const s = trimNonEmpty(value);
  if (!s) return false;
  const t = new Date(s).getTime();
  return !Number.isNaN(t);
}

export function DetailPageHelmet({
  pageTitle,
  metaDescription,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noIndex = false,
  articlePublishedTime,
  author,
  breadcrumbItems,
  seoReady = true,
}) {
  const title = trimNonEmpty(pageTitle) || DEFAULT_TITLE;
  const description = trimNonEmpty(metaDescription) || DEFAULT_DESCRIPTION;
  const canonical = trimNonEmpty(canonicalUrl);
  const image = trimNonEmpty(ogImage) || getDefaultOgImageUrl();
  const ogTypeSafe = ogType === 'article' ? 'article' : 'website';
  const showArticleTime = isValidDateString(articlePublishedTime);
  const articleTimeContent = showArticleTime ? trimNonEmpty(articlePublishedTime) : '';
  const authorName = trimNonEmpty(author);

  const siteUrl = getPublicSiteUrl();

  const jsonLdData = useMemo(() => {
    const graph = [];
    if (ogTypeSafe === 'article' && canonical) {
      graph.push(
        buildArticleJsonLd({
          headline: articleHeadlineFromTitle(title),
          description,
          url: canonical,
          image: image || undefined,
          datePublished: showArticleTime ? articleTimeContent : undefined,
          author: authorName || undefined,
        })
      );
    }
    if (Array.isArray(breadcrumbItems) && breadcrumbItems.length > 0 && siteUrl) {
      const crumbs = breadcrumbItems
        .map((item) => {
          const name = trimNonEmpty(item.name);
          if (!name) return null;
          const explicitUrl = trimNonEmpty(item.url);
          const path = trimNonEmpty(item.path);
          const url = explicitUrl || (path ? `${siteUrl}${path.startsWith('/') ? path : `/${path}`}` : '');
          if (!url) return null;
          return { name, url };
        })
        .filter(Boolean);
      graph.push(buildBreadcrumbJsonLd(crumbs));
    }
    return graph;
  }, [
    ogTypeSafe,
    canonical,
    title,
    description,
    image,
    showArticleTime,
    articleTimeContent,
    authorName,
    breadcrumbItems,
    siteUrl,
  ]);

  usePrerenderReady(Boolean(seoReady));

  return (
    <>
    {jsonLdData.length > 0 ? <JsonLdScript data={jsonLdData} /> : null}
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:type" content={ogTypeSafe} />
      {image ? <meta property="og:image" content={image} /> : null}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image ? <meta name="twitter:image" content={image} /> : null}
      {showArticleTime && articleTimeContent ? (
        <meta property="article:published_time" content={articleTimeContent} />
      ) : null}
      {authorName ? <meta name="author" content={authorName} /> : null}
      {noIndex ? <meta name="robots" content="noindex" /> : null}
    </Helmet>
    </>
  );
}
